import { useEffect, useRef, useState, type ReactNode } from "react";
import { Backdrop, Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { nurseService } from "src/api/nurse/nurseService";
import { PatientChartSummaryData } from "src/api/nurse/nurseServiceDtos";
import PatientDatas from "src/modules/medcard/patient-datas";
import TreatmentApplicationsModal from "src/modules/nurse/order-medicine";
import TopBar from "./components/top-bar";
import GeneralInfo from "./components/general-info";
import FormInfos from "./components/form-infos";
import LabResults from "./components/lab-results";
import TitlesComponent from "./components/titles-component";
import TreatmentsPanel from "./components/treatments-panel";
import MonitoringsPanel from "./components/monitorings-panel";
import OpenedMedcardsPanel from "./components/opened-medcards-panel";
import PatientProcessPanel from "./components/patient-process-panel";
import { OpenedMedcards } from "src/modules/medcard/opened-medcars";

const emptySummary: PatientChartSummaryData = {
  lengthofstaydays: null,
  activemedicationcount: null,
  panicvaluecount: null,
  allergycount: null,
  fluidbalance: null,
  painscale: null,
  bloodsugartrackingvalue: null,
  nrsassessment: null,
};

type PatientTableProps = {
  patientId?: number | null;
};

type PatientTablePatientInfo = {
  upn?: string;
  patientName?: string;
  gender?: string;
  isPregnant?: boolean;
};

type PatientTableTab = "all" | "lab" | "treatments" | "monitorings" | "opened-cards" | "processes";

/** "Tümü" görünümünde ağır panelleri sadece kaydırınca mount et; tekil sekmede her zaman yükle. */
function PatientTableDeferredSection({
  defer,
  patientId,
  minHeight = 220,
  children,
}: Readonly<{
  defer: boolean;
  patientId?: number;
  minHeight?: number;
  children: ReactNode;
}>) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(() => !defer);

  useEffect(() => {
    if (!defer) {
      setMounted(true);
    }
  }, [defer]);

  useEffect(() => {
    setMounted(false);
  }, [patientId]);

  useEffect(() => {
    if (!defer || mounted) {
      return;
    }
    const el = sentinelRef.current;
    if (!el) {
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMounted(true);
        }
      },
      { root: null, rootMargin: "280px 0px 360px 0px", threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [defer, mounted, patientId]);

  return (
    <>
      {defer && !mounted && (
        <Box
          ref={sentinelRef}
          sx={{
            minHeight,
            borderRadius: 1,
            bgcolor: "action.hover",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}
      {(!defer || mounted) && children}
    </>
  );
}

export default function PatientTable({ patientId: patientIdProp }: Readonly<PatientTableProps>) {
  const navigate = useNavigate();
  const { id } = useParams();
  const patientId = patientIdProp ?? (id ? Number(id) : undefined);
  const [summary, setSummary] = useState<PatientChartSummaryData>(emptySummary);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryLoaded, setSummaryLoaded] = useState(false);
  const [treatmentModalOpen, setTreatmentModalOpen] = useState(false);
  const [openedCardsModalOpen, setOpenedCardsModalOpen] = useState(false);
  const [patientInfo, setPatientInfo] = useState<PatientTablePatientInfo>({});
  const [activeTab, setActiveTab] = useState<PatientTableTab>("all");
  const topSectionRef = useRef<HTMLDivElement | null>(null);
  const labSectionRef = useRef<HTMLDivElement | null>(null);
  const treatmentSectionRef = useRef<HTMLDivElement | null>(null);
  const monitoringSectionRef = useRef<HTMLDivElement | null>(null);
  const openedCardsSectionRef = useRef<HTMLDivElement | null>(null);
  const processesSectionRef = useRef<HTMLDivElement | null>(null);

  const deferHeavyPanels = activeTab === "all";

  useEffect(() => {
    let mounted = true;

    const fetchSummary = async () => {
      if (!patientId) {
        setSummary(emptySummary);
        setSummaryLoaded(true);
        return;
      }

      setSummaryLoading(true);
      setSummaryLoaded(false);
      try {
        const response = await nurseService.patient.getPatientChartSummary({ ptId: patientId });
        if (mounted) {
          setSummary({ ...emptySummary, ...response });
          setSummaryLoaded(true);
        }
      } catch {
        if (mounted) {
          setSummary(emptySummary);
          setSummaryLoaded(true);
        }
      } finally {
        if (mounted) {
          setSummaryLoading(false);
        }
      }
    };

    fetchSummary();

    return () => {
      mounted = false;
    };
  }, [patientId]);

  const handleTopBarChange = (tab: PatientTableTab) => {
    setActiveTab(tab);

    if (tab === "lab") {
      labSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (tab === "treatments") {
      treatmentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (tab === "monitorings") {
      monitoringSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (tab === "opened-cards") {
      openedCardsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (tab === "processes") {
      topSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (tab === "all") {
      topSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const showSummaryAndForms = activeTab === "all";
  const showLabSection = activeTab === "all" || activeTab === "lab";
  const showTreatmentSection = activeTab === "all" || activeTab === "treatments";
  const showMonitoringSection = activeTab === "all" || activeTab === "monitorings";
  const showOpenedCardsSection = activeTab === "all" || activeTab === "opened-cards";
  const showProcessesSection = activeTab === "all" || activeTab === "processes";

  return (
    <Box sx={{ p: 2 }} ref={topSectionRef}>
      <PatientDatas
        key={`patient-table-header-${patientId ?? "none"}`}
        isCreateMedCard
        isOpenMedCard
        patientId={patientId}
        patientDataOut={(data: any) => {
          const patientData = data?.data;
          if (!patientData) return;

          setPatientInfo({
            upn: patientData.upn || patientData.protid || patientData.protId,
            patientName: patientData.patientfullname || patientData.patientFullName || patientData.name,
            gender: patientData.gender || patientData.sex || patientData.sexuality,
            isPregnant: patientData.pregnantorlactation === "D" || patientData.ispregnant === true || patientData.isPregnant === true,
          });
        }}
      />

      <Box sx={{ mt: "18px" }}>
        <TopBar key={`patient-table-topbar-${patientId ?? "none"}`} onTabChange={handleTopBarChange} />
      </Box>

      {showSummaryAndForms && (
        <>
          <Box sx={{ mt: "18px" }}>
            <GeneralInfo key={`patient-table-summary-${patientId ?? "none"}`} summary={summary} loading={summaryLoading} />
          </Box>

          <Box sx={{ mt: "18px" }}>
            <FormInfos key={`patient-table-forms-${patientId ?? "none"}`} patientId={patientId} summary={summary} loading={summaryLoading} />
          </Box>
        </>
      )}

      {showLabSection && (
        <Box sx={{ mt: "18px" }} ref={labSectionRef}>
          <PatientTableDeferredSection defer={deferHeavyPanels} patientId={patientId} minHeight={280}>
            <LabResults key={`patient-table-labs-${patientId ?? "none"}`} />
          </PatientTableDeferredSection>
        </Box>
      )}

      {showTreatmentSection && (
        <Box sx={{ mt: "18px" }} ref={treatmentSectionRef}>
          <PatientTableDeferredSection defer={deferHeavyPanels} patientId={patientId} minHeight={360}>
            <TitlesComponent title="Tedaviler" onExpand={() => setTreatmentModalOpen(true)} />
            <TreatmentsPanel patientId={patientId} upn={patientInfo.upn} patientName={patientInfo.patientName} />
          </PatientTableDeferredSection>
        </Box>
      )}

      {showMonitoringSection && (
        <Box sx={{ mt: "18px" }} ref={monitoringSectionRef}>
          <PatientTableDeferredSection defer={deferHeavyPanels} patientId={patientId} minHeight={280}>
            <TitlesComponent
              title="İzlemler"
              onExpand={() => {
                if (!patientId) return;
                navigate(`/medcard/patient-monitoring/${patientId}`);
              }}
            />
            <MonitoringsPanel patientId={patientId} />
          </PatientTableDeferredSection>
        </Box>
      )}

      {showOpenedCardsSection && (
        <Box sx={{ mt: "18px" }} ref={openedCardsSectionRef}>
          <PatientTableDeferredSection defer={deferHeavyPanels} patientId={patientId} minHeight={320}>
            <TitlesComponent title="Açılan Kartlar" onExpand={() => setOpenedCardsModalOpen(true)} />
            <OpenedMedcardsPanel
              patientId={patientId}
              upn={patientInfo.upn}
              gender={patientInfo.gender}
              onOpenFullModal={() => setOpenedCardsModalOpen(true)}
            />
          </PatientTableDeferredSection>
        </Box>
      )}

      {showProcessesSection && (
        <Box sx={{ mt: "18px" }} ref={processesSectionRef}>
          <PatientTableDeferredSection defer={deferHeavyPanels} patientId={patientId} minHeight={360}>
            <TitlesComponent
              title="İşlemler"
              onExpand={() => {
                if (!patientId) return;
                navigate(`/nurse/patient-process/${patientId}`);
              }}
            />
            <PatientProcessPanel
              patientId={patientId}
              patientMeta={{
                displayName: patientInfo.patientName,
                upn: patientInfo.upn,
              }}
            />
          </PatientTableDeferredSection>
        </Box>
      )}

      <Backdrop
        open={Boolean(patientId) && summaryLoading && !summaryLoaded}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 10,
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <TreatmentApplicationsModal
        open={treatmentModalOpen}
        onClose={() => setTreatmentModalOpen(false)}
        patientId={patientId}
        upn={patientInfo.upn}
        patientName={patientInfo.patientName}
        gender={patientInfo.gender}
        isPregnant={patientInfo.isPregnant}
      />
      <OpenedMedcards
        open={openedCardsModalOpen}
        onClose={() => setOpenedCardsModalOpen(false)}
        ptId={patientId ? String(patientId) : undefined}
        upn={patientInfo.upn}
        gender={patientInfo.gender}
      />
    </Box>
  );
}
